import React, { useState, useMemo } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { Card, Form } from 'react-bootstrap'
import { ExportRow, TableExportActions, sanitizeExportFilename } from '../_table/table-export-actions'

interface ClickDataTableProps<T> {
    title: string
    columns: TableColumn<T>[]
    data: T[]
    height?:string
    id:string
    paginationPerPage?: number
    paginationRowsPerPageOptions?: number[]
    headerActions?: React.ReactNode
    showTitle?: boolean
    showSearch?: boolean
    showExportActions?: boolean
    headerActionsFullWidth?: boolean
}

const toExportValue = (value: unknown): string | number | boolean | null | undefined => {
    if (value == null) return ''
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
    if (Array.isArray(value)) return value.join(', ')
    if (value instanceof Date) return value.toISOString()
    return String(value)
}

const toColumnLabel = <T extends { [key: string]: any }>(column: TableColumn<T>, index: number) => {
    if (typeof column.name === 'string') return column.name
    return `Column ${index + 1}`
}

const buildExportRows = <T extends { [key: string]: any }>(columns: TableColumn<T>[], rows: T[]): ExportRow[] => {
    const exportableColumns = columns.filter((column) => !column.omit)

    return rows.map((row, rowIndex) => {
        return exportableColumns.reduce<ExportRow>((accumulator, column, columnIndex) => {
            const label = toColumnLabel(column, columnIndex)
            const formattedValue = column.format ? column.format(row, rowIndex) : undefined
            const selectedValue = typeof formattedValue !== 'undefined'
                ? formattedValue
                : typeof column.selector === 'function'
                    ? column.selector(row, rowIndex)
                    : row[label]

            accumulator[label] = toExportValue(selectedValue)
            return accumulator
        }, {})
    })
}

export function ClickDataTable<T extends { [key: string]: any }>({
    title,
    columns,
    data,
    height,
    id,
    paginationPerPage = 50,
    paginationRowsPerPageOptions = [50, 100, 200, 500],
    headerActions,
    showTitle = true,
    showSearch = true,
    showExportActions = true,
    headerActionsFullWidth = false
}: ClickDataTableProps<T>) {
    const [filterText, setFilterText] = useState('')
    const showHeader = showTitle || showSearch || showExportActions || !!headerActions

    const filteredData = useMemo(() => {
        if (!filterText) return data
        return data.filter(row =>
            Object.values(row)
                .join(' ')
                .toLowerCase()
                .includes(filterText.toLowerCase())
        )
    }, [data, filterText])

    const exportRows = useMemo(() => buildExportRows(columns, filteredData), [columns, filteredData])
    const exportFilename = useMemo(() => sanitizeExportFilename(title), [title])

    return (
    <div>
            <Card className="mb-3 shadow-sm border-0">
                {!showHeader ? null : <Card.Header className="bg-white border-0 pb-0 pt-3 px-3">
                    <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-3">
                        <div>
                            {!showTitle ? null : <h5 className="mb-1">{title}</h5>}
                            {!showSearch ? null : <div style={{ maxWidth: '320px' }}>
                                <Form.Group controlId={`${title}-search`}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search..."
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                        size="sm"
                                    />
                                </Form.Group>
                            </div>}
                        </div>

                        <div className={headerActionsFullWidth ? 'w-100' : 'd-flex flex-wrap gap-2 justify-content-lg-end ms-lg-auto'}>
                            {!showExportActions ? null : <TableExportActions
                                data={exportRows}
                                filename={exportFilename}
                                className="d-flex flex-wrap gap-2 justify-content-lg-end"
                                disabled={exportRows.length === 0}
                            />}
                            {headerActions}
                        </div>
                    </div>
                </Card.Header>}
                <Card.Body>
                    <div>
                        <DataTable
                            columns={columns}
                            data={filteredData}
                            defaultSortFieldId={id}
                            defaultSortAsc={false}
                            dense
                            responsive
                            fixedHeader
                            pagination
                            paginationPerPage={paginationPerPage}
                            paginationRowsPerPageOptions={paginationRowsPerPageOptions}
                            fixedHeaderScrollHeight={height || '300px'}

                            customStyles={{
                                table:{style:{width:'400px', minWidth: '100%'}},
                                cells: {
                                    style: {
                                        borderBottom: '1px solid rgba(0,0,0,.12)',
                                    },

                                },
                                rows: {
                                    style: {
                                        borderBottom: 'none !important',
                                    },
                                },
                                headRow: {
                                    style: {
                                        borderBottom: 'none !important',
                                    },
                                },
                                headCells: {
                                    style: {
                                        borderBottom: '1px solid rgba(0,0,0,.12)',
                                        backgroundColor:'#fff'
                                    },
                                }
                            }}
                        />
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}
